// src/sites/Topbar.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useI18n } from "../I18nProvider"; // ⬅️ aus deinem Provider holen
import "./styles.css";

function useActiveSection(sectionIds) {
  const [active, setActive] = useState(sectionIds[0] || "");
  useEffect(() => {
    const handler = () => {
      let current = sectionIds[0] || "";
      sectionIds.forEach((id) => {
        const el = document.getElementById(id);
        if (!el) return;
        const rect = el.getBoundingClientRect();
        if (rect.top <= 120 && rect.bottom >= 200) current = id;
      });
      setActive(current);
    };
    handler();
    window.addEventListener("scroll", handler, { passive: true });
    window.addEventListener("resize", handler);
    return () => {
      window.removeEventListener("scroll", handler);
      window.removeEventListener("resize", handler);
    };
  }, [sectionIds]);
  return active;
}

export default function Topbar() {
  const { lang, setLang, t } = useI18n();        // ⬅️ kommt aus dem globalen Provider
  const [menuOpen, setMenuOpen] = useState(false);
  const active = useActiveSection(["home", "services", "vehicle-purchase", "fahrzeuge", "about", "contact"]);

  const navigate = useNavigate();
  const location = useLocation();
  const isHomePage = location.pathname === "/";

  // Body scroll lock für Mobile-Menü
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  // Auto-close on desktop resize
  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > 900) setMenuOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const closeMenu = () => setMenuOpen(false);

  return (
    <>

      <nav className="navbar">
        <div className="nav-container">
          {/* Logo und Schriftzug gestapelt als EINE Marke: das Bild oben,
              der Name direkt darunter. Groesse und Abstaende in styles.css
              unter .marke, damit es am Handy mitwachsen kann. */}
          <button
            type="button"
            className="marke"
            onClick={() => {
              closeMenu();
              if (isHomePage) {
                window.scrollTo({ top: 0, behavior: "smooth" });
              } else {
                navigate("/");
              }
            }}
          >
            <span className="marke-glanz">
              <img
                src="/images/logo-mah-blau2.webp"
                alt=""
                className="marke-bild"
                aria-hidden="true"
              />
            </span>
            <span className="marke-name">AUTO M.A.H.</span>
          </button>

          {/* Hauptaktion in der Kopfzeile. Weil die Kopfzeile beim Scrollen
              stehen bleibt, ist das Ankauf-Fenster von jeder Stelle der Seite
              aus einen Klick entfernt.
              Die Startseite hoert auf das Ereignis "automah:formular" und
              oeffnet das Fenster. Auf Unterseiten (Impressum, Datenschutz)
              gibt es sie nicht - dort wird erst zur Startseite gewechselt. */}
          <button
            type="button"
            className="kopf-cta"
            onClick={() => {
              const oeffnen = () =>
                window.dispatchEvent(
                  new CustomEvent("automah:formular", { detail: "purchase" })
                );
              closeMenu();
              if (isHomePage) {
                oeffnen();
              } else {
                navigate("/");
                setTimeout(oeffnen, 120);
              }
            }}
          >
            {t("nav_cta")}
          </button>

          {/* Am Handy: Sprachen in die Luecke zwischen Logo und Menue.
             Am Rechner bleibt der Umschalter im normalen Menue. */}
          <div className="kopf-sprachen" role="group" aria-label="Sprache">
            {["de", "en", "fr"].map((code) => (
              <button
                key={code}
                className={lang === code ? "active" : ""}
                type="button"
                onClick={() => setLang(code)}
              >
                {code.toUpperCase()}
              </button>
            ))}
          </div>

          <button
            className="mobile-menu-toggle"
            id="mobileMenuToggle"
            aria-label="Menü"
            aria-expanded={menuOpen}
            aria-controls="navMenu"
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
          >
            <i className="fas fa-bars" aria-hidden="true"></i>
          </button>

          <ul className={`nav-menu ${menuOpen ? "active" : ""}`} id="navMenu" role="menu">
            {/* Nur Ankerlinks auf der Startseite */}
            {/* Menue steht IMMER, auch auf Impressum/Datenschutz/AGB.
                Vorher war es dort ausgeblendet - der einzige Rueckweg war das
                Logo, und darauf kommt nicht jeder. Auf einer Unterseite fuehrt
                ein Klick zur Startseite und scrollt dann zum Abschnitt. */}
            {[
              ["home", "nav_home"],
              ["services", "nav_services"],
              ["vehicle-purchase", "nav_purchase"],
              ["fahrzeuge", "nav_cars"],
              ["about", "nav_about"],
              ["contact", "nav_contact"],
            ].map(([id, schluessel]) => (
              <li role="none" key={id}>
                <a
                  href={isHomePage ? `#${id}` : `/#${id}`}
                  className={isHomePage && active === id ? "active" : ""}
                  onClick={(e) => {
                    closeMenu();
                    if (id === "fahrzeuge") {
                      e.preventDefault();
                      const oeffnen = () =>
                        window.dispatchEvent(new CustomEvent("automah:fahrzeuge"));
                      if (isHomePage) oeffnen();
                      else {
                        navigate("/");
                        setTimeout(oeffnen, 160);
                      }
                      return;
                    }
                    if (isHomePage) return;
                    e.preventDefault();
                    navigate("/");
                    setTimeout(() => {
                      const ziel = document.getElementById(id);
                      if (ziel) {
                        const oben = ziel.getBoundingClientRect().top + window.scrollY - 80;
                        window.scrollTo({ top: Math.max(0, oben), behavior: "smooth" });
                      }
                    }, 140);
                  }}
                >
                  {t(schluessel)}
                </a>
              </li>
            ))}

            {/* Sprachumschalter – immer sichtbar */}
            <li className="language-container" role="none">
              <div className="language-switcher" role="group" aria-label="Sprache">
                {["de", "en", "fr"].map((code) => (
                  <button
                    key={code}
                    className={lang === code ? "active" : ""}
                    onClick={() => {
                      setLang(code);   // ⬅️ upd. globalen Zustand -> ganze App re-rendert ohne Reload
                      setMenuOpen(false);
                    }}
                    type="button"
                  >
                    {code.toUpperCase()}
                  </button>
                ))}
              </div>
            </li>
          </ul>
        </div>
      </nav>
    </>
  );
}
